/**
 * Game catalog domain types.
 *
 * The catalog is plain data (see public/catalog.json and docs/catalog-spec.md).
 * Nothing in this module depends on React, so the same definitions can later be
 * shared with an API, a publishing CLI, or game repositories.
 */

export const CATALOG_SCHEMA_VERSION = 1

/**
 * - `published`: listed and playable once a build URL is available.
 * - `coming-soon`: listed, no public build yet.
 * - `unavailable`: listed, temporarily not playable (e.g. a broken build was pulled).
 * - `hidden`: kept in the catalog file but not listed or routable.
 */
export const GAME_STATUSES = ['published', 'coming-soon', 'unavailable', 'hidden'] as const
export type GameStatus = (typeof GAME_STATUSES)[number]

export const INPUT_METHODS = ['keyboard', 'mouse', 'touch', 'gamepad'] as const
export type InputMethod = (typeof INPUT_METHODS)[number]

export interface ControlHint {
  /** Keys or gestures, e.g. ["←", "→"] or ["Space"]. */
  keys: string[]
  /** What the input does, e.g. "Move". */
  action: string
}

export interface GameControls {
  inputs: InputMethod[]
  hints: ControlHint[]
}

export interface GameEntry {
  /**
   * The game's browser entry point (an HTML page loaded in an iframe).
   * Absolute URL, or a path relative to VITE_GAMES_BASE_URL. `null` when no
   * playable build has been configured.
   */
  url: string | null
  /**
   * Release manifest naming the active build (e.g. a game's current.json on
   * the games domain). Fetched each time a play session starts, so publishing
   * a new version needs no website deploy. Takes precedence over `url`.
   */
  manifest: string | null
  /** Width ÷ height of the game's viewport. `null` lets the game fill the stage. */
  aspectRatio: number | null
  /**
   * When true, the player waits for a `zarcade:ready` postMessage from the game
   * before hiding the loading screen, instead of relying on the iframe `load` event.
   */
  readySignal: boolean
}

export interface Game {
  /** URL-safe unique identifier; also the route segment `/games/{id}`. */
  id: string
  title: string
  /** One-line pitch used on cards. */
  tagline: string
  description: string
  developer: string
  genres: string[]
  /** Thumbnail image URL, or null to use generated cover art. */
  thumbnail: string | null
  /** Version of the currently active build, or null when none is published. */
  version: string | null
  entry: GameEntry
  controls: GameControls
  status: GameStatus
  /** ISO date (YYYY-MM-DD) of public release, or null if unreleased. */
  releaseDate: string | null
  /** ISO date (YYYY-MM-DD) the game was added to the catalog. */
  addedAt: string
}

export interface Catalog {
  schemaVersion: typeof CATALOG_SCHEMA_VERSION
  /** Id of a game to promote. Reserved; not currently displayed. */
  featured: string | null
  /** Listed games (hidden games are removed during loading). */
  games: Game[]
}
